import { isApiError } from '@hive/api';
import { useI18n, type CommonKey } from '@hive/i18n';
import { useCallback, useEffect, useRef, useState, type DependencyList, type ReactNode } from 'react';
import { Icon } from './icons';
import { useToast } from './overlay';

// ---------------------------------------------------------------- diagnóstico passo a passo

export interface DiagStep {
  label: ReactNode;
  state: 'ok' | 'fail' | 'run' | 'wait';
  detail?: ReactNode;
  ms?: number | null;
}

/** Lista de passos de um teste ("Testar conexão"), com estado por forma e cor. */
export function DiagList({ steps, label }: { steps: DiagStep[]; label?: string }) {
  const { t, fmt } = useI18n<CommonKey>();
  return (
    <ol className="diag" aria-label={label} aria-live="polite">
      {steps.map((s, i) => (
        <li key={i}>
          <span className={s.state === 'ok' ? 'dot-ok' : s.state === 'fail' ? 'dot-fail' : s.state === 'run' ? 'dot-run' : 'dot-wait'} aria-hidden="true">
            {s.state === 'ok' ? '✓' : s.state === 'fail' ? '!' : ''}
          </span>
          <span>
            {s.label}
            {s.detail && (
              <>
                <br />
                <span className="muted small">{s.detail}</span>
              </>
            )}
          </span>
          <span className="ms">
            {s.state === 'ok' || s.state === 'fail' ? (s.ms != null ? `${fmt.number(s.ms)} ms` : '') : s.state === 'run' ? t('diag.running') : t('diag.waiting')}
          </span>
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------- copiar

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** Copia um texto (token, link, comando). Sem permissão de área de transferência, seleciona o texto. */
export function CopyButton({ text, targetId, label, className }: { text: string; targetId?: string; label?: string; className?: string }) {
  const { t } = useI18n<CommonKey>();
  const toast = useToast();
  return (
    <button
      type="button"
      className={className ?? 'btn btn-sm'}
      onClick={async () => {
        if (await copyText(text)) {
          toast(t('ui.copied'), { kind: 'ok' });
          return;
        }
        const n = targetId ? document.getElementById(targetId) : null;
        if (n) {
          const r = document.createRange();
          r.selectNodeContents(n);
          const s = window.getSelection();
          s?.removeAllRanges();
          s?.addRange(r);
        }
        toast(t('ui.copyFallback'), { kind: 'info' });
      }}
    >
      <Icon name="copy" size="sm" />
      {label ?? t('ui.copy')}
    </button>
  );
}

// ---------------------------------------------------------------- carregamento de dados

export interface Loaded<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => void;
  setData: (fn: (d: T | undefined) => T | undefined) => void;
}

/** Carrega dados de uma função assíncrona, com recarga e estado de erro (sem biblioteca externa). */
export function useLoad<T>(fn: () => Promise<T>, deps: DependencyList): Loaded<T> {
  const [data, setDataState] = useState<T>();
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);
  const [tick, setTick] = useState(0);
  const fnRef = useRef(fn);
  fnRef.current = fn;
  useEffect(() => {
    let alive = true;
    setLoading(true);
    fnRef
      .current()
      .then((d) => {
        if (!alive) return;
        setDataState(d);
        setError(null);
      })
      .catch((e: unknown) => {
        if (alive) setError(e);
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- as dependências vêm de quem chama
  }, [...deps, tick]);
  const reload = useCallback(() => setTick((x) => x + 1), []);
  const setData = useCallback((f: (d: T | undefined) => T | undefined) => setDataState(f), []);
  return { data, error, loading, reload, setData };
}

/** Erro 403: a ação não é permitida para o perfil ativo. */
export function isForbidden(e: unknown): boolean {
  return isApiError(e) && e.status === 403;
}

// ---------------------------------------------------------------- idade em dias

/** "hoje", "1 dia", "9 dias" (idade em dias inteiros, ex.: assinaturas do antivírus); null/negativo vira "—". */
export function daysText(t: (key: 'ui.age.today' | 'ui.age.days', params?: { count: number }) => string, n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n) || n < 0) return '—';
  const d = Math.floor(n);
  return d === 0 ? t('ui.age.today') : t('ui.age.days', { count: d });
}

/** `daysText` com o idioma da tela. */
export function useDaysText(): (n: number | null | undefined) => string {
  const { t } = useI18n<CommonKey>();
  return (n) => daysText(t, n);
}
